import type { Directive } from 'vue'
import { isNear, radToDegrees } from '@/utils/utils'

interface FlickOptions {
    minVelocity?: number // px per ms required to qualify as a flick
    velocitySampleDuration?: number // time span (ms) used to estimate speed
    overshoot?: number // extra px beyond the edge for a satisfying exit
    onFlick?: ((el: HTMLElement) => void) | null
    onDrag?: ((dragCompletePercentage: number) => void) | null
    dragStop?: ((willClose: boolean) => void) | null
}

const defaultOpts: Required<FlickOptions> = {
    minVelocity: 0.7,
    velocitySampleDuration: 150,
    overshoot: 40,
    onFlick: null,
    onDrag: null,
    dragStop: null,
}

function registerHandlers(el: HTMLElement, opts: Required<FlickOptions>) {
    let startPoint = { x: 0, y: 0 }
    let lastPoint = { x: 0, y: 0 }
    let lastTimestamp = 0
    const dxs: number[] = []
    const dTimes: number[] = []
    let validFlickStarted: boolean | undefined
    let dragHappened = false
    const stylesToRestore: [HTMLElement, string, string][] = []

    function onStart(e: TouchEvent) {
        const { screenX: x, screenY: y } = e.touches[0]
        startPoint = { x, y }
        lastPoint = { ...startPoint }
        lastTimestamp = performance.now()
        dxs.length = 0
        dTimes.length = 0
        validFlickStarted = undefined
        dragHappened = false
        stylesToRestore.length = 0

        el.style.touchAction = 'pan-y'

        setStyleAndSaveOriginal(el, 'transition', 'none')
        setStyleAndSaveOriginal(el, 'transform', 'none')
        setStyleAndSaveOriginal(el, 'will-change', 'transform')
    }

    function onMove(e: TouchEvent) {
        if (validFlickStarted === false) return

        const { screenX: x, screenY: y } = e.touches[0]
        const now = performance.now()

        if (validFlickStarted === undefined) {
            const angle = radToDegrees(Math.atan2(startPoint.x - x, startPoint.y - y))
            const isHorizontalDrag = isNear(angle, -90, 25)
            const isVerticalDrag = isNear(angle, -180, 45) || isNear(angle, 0, 45)

            if (isHorizontalDrag) {
                validFlickStarted = true
                e.preventDefault()
            } else if (isVerticalDrag) {
                validFlickStarted = false
                if (Math.abs(lastPoint.x - startPoint.x) >= 2) resetModalPosition()
                return
            }
        }

        dxs.push(x - lastPoint.x)
        lastPoint = { x, y }

        dTimes.push(now - lastTimestamp)

        while (dTimes.reduce((s, v) => s + v, 0) > opts.velocitySampleDuration) {
            dxs.shift()
            dTimes.shift()
        }

        const translateX = Math.max(0, x - startPoint.x)
        if (translateX > 1) {
            dragHappened = true
            el.style.transform = `translateX(${translateX}px)`
        }
        lastTimestamp = now

        if (opts.onDrag && dragHappened) {
            const dragCompletePercentage = (lastPoint.x - startPoint.x) / window.innerWidth
            opts.onDrag(dragCompletePercentage)
        }
    }

    function onEnd() {
        restoreSavedStyles()

        const travelled = dxs.reduce((sum, dx) => sum + dx, 0)

        const time = dTimes.reduce((sum, dt) => sum + dt, 0) || 1
        const velocity = travelled / time // px/ms

        const dragCompletePercentage = (lastPoint.x - startPoint.x) / window.innerWidth

        const doCompleteFlick =
            validFlickStarted && (dragCompletePercentage > 0.6 || velocity >= opts.minVelocity)

        if (!doCompleteFlick) {
            if (opts.dragStop && dragHappened) opts.dragStop(false)
            if (Math.abs(lastPoint.x - startPoint.x) >= 2) resetModalPosition()
            return
        }

        const rect = el.getBoundingClientRect()
        const distance = window.innerWidth - rect.left + rect.width + opts.overshoot
        const capedVelocity = Math.max(velocity, 1)
        const duration = distance / Math.min(capedVelocity, 2)

        el.style.transition = `transform ${duration}ms linear`
        el.style.transform = `translateX(${distance}px)`

        setTimeout(() => restoreSavedStyles, duration)

        if (opts.dragStop && dragHappened) opts.dragStop(true)
        if (opts.onFlick) setTimeout(opts.onFlick, duration)
    }

    function resetModalPosition() {
        const duration = 200
        el.style.transition = `transform ${duration}ms ease-out`
        el.style.transform = ''
        setTimeout(restoreSavedStyles, duration)
    }

    function setStyleAndSaveOriginal(el: HTMLElement, prop: string, value: string) {
        // @ts-expect-error string not compatible with keyof Style
        stylesToRestore.push([el, prop, el.style[prop]])
        // @ts-expect-error string not compatible with keyof Style
        el.style[prop] = value
    }
    function restoreSavedStyles() {
        stylesToRestore.forEach(([el, prop, value]) => {
            // @ts-expect-error string not compatible with keyof Style
            el.style[prop] = value
        })
    }

    return { onStart, onMove, onEnd }
}

const vFlick: Directive<HTMLElement, FlickOptions | undefined> = {
    mounted(el, binding) {
        const opts = { ...defaultOpts, ...binding.value }
        const handlers = registerHandlers(el, opts)

        el.addEventListener('touchstart', handlers.onStart, { passive: true })
        el.addEventListener('touchmove', handlers.onMove, { passive: false })
        el.addEventListener('touchend', handlers.onEnd)

        // keep references so we can unbind later
        ;(el as any).flickHandlers = handlers
    },

    unmounted(el) {
        const handlers = (el as any).flickHandlers
        if (!handlers) return

        el.removeEventListener('touchstart', handlers.onStart)
        el.removeEventListener('touchmove', handlers.onMove)
        el.removeEventListener('touchend', handlers.onEnd)
        delete (el as any).flickHandlers
    },
}

export default vFlick
